import { type ReactNode } from "react";
import { ChevronRight, Folder, Loader2, Search, X } from "lucide-react";
import { MailModalOverlay } from "@/features/shared/MailModalOverlay";
import { cn } from "@/lib/cn";

type Props = {
  open: boolean;
  title: string;
  subtitle?: string;
  busy?: boolean;
  busyLabel?: string;
  search: string;
  onSearchChange: (value: string) => void;
  crumbs: string[];
  onCrumbClick: (index: number) => void;
  onClose: () => void;
  footer: ReactNode;
  children: ReactNode;
  emptyLabel?: string;
  loading?: boolean;
  /** Sans blur empilé — transparent sur le tiroir compose, stacked en réponse inline. */
  overlayBackdrop?: "default" | "transparent" | "stacked";
};

export function ComposeSuitePickModal({
  open,
  title,
  subtitle,
  busy = false,
  busyLabel = "Chargement…",
  search,
  onSearchChange,
  crumbs,
  onCrumbClick,
  onClose,
  footer,
  children,
  emptyLabel = "Aucun élément",
  loading = false,
  overlayBackdrop = "transparent",
}: Props) {
  return (
    <MailModalOverlay
      open={open}
      onClose={onClose}
      labelledBy="compose-suite-pick-title"
      variant="bottom"
      backdrop={overlayBackdrop}
      animation="fade"
      panelClassName="compose-suite-pick-modal max-w-lg sm:max-w-xl"
    >
      <div className="compose-suite-pick-modal__header">
        <div className="min-w-0 flex-1">
          <h2 id="compose-suite-pick-title" className="truncate text-sm font-semibold text-[var(--mail-text)]">
            {title}
          </h2>
          {subtitle ? (
            <p className="truncate text-[11px] text-[var(--mail-muted)]">{subtitle}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="compose-suite-pick-modal__close"
          aria-label="Fermer"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="compose-suite-pick-modal__toolbar">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--mail-muted)]" />
          <input
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Rechercher…"
            className="compose-suite-pick-modal__search"
            disabled={busy}
          />
        </div>
      </div>

      <nav className="compose-suite-pick-modal__crumbs" aria-label="Chemin">
        <button
          type="button"
          className="compose-suite-pick-modal__crumb"
          onClick={() => onCrumbClick(-1)}
          disabled={busy}
        >
          Racine
        </button>
        {crumbs.map((part, index) => (
          <span key={`${part}-${index}`} className="inline-flex min-w-0 items-center gap-0.5">
            <ChevronRight className="h-3 w-3 shrink-0 text-[var(--mail-muted)]" aria-hidden />
            <button
              type="button"
              className="compose-suite-pick-modal__crumb truncate"
              onClick={() => onCrumbClick(index)}
              disabled={busy}
              title={part}
            >
              {part}
            </button>
          </span>
        ))}
      </nav>

      <div className="compose-suite-pick-modal__body" aria-busy={loading || busy}>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-[var(--mail-muted)]">
            <Loader2 className="h-4 w-4 animate-spin text-[var(--mail-accent)]" aria-hidden />
            Chargement…
          </div>
        ) : busy ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-[var(--mail-muted)]">
            <Loader2 className="h-4 w-4 animate-spin text-[var(--mail-accent)]" aria-hidden />
            {busyLabel}
          </div>
        ) : (
          children
        )}
        {!loading && !busy && emptyLabel ? null : null}
      </div>

      {footer}
    </MailModalOverlay>
  );
}

type RowProps = {
  kind: "folder" | "file";
  label: string;
  meta?: string;
  selected?: boolean;
  disabled?: boolean;
  thumbnailUrl?: string;
  onClick: () => void;
};

export function ComposeSuitePickRow({
  kind,
  label,
  meta,
  selected = false,
  disabled = false,
  thumbnailUrl,
  onClick,
}: RowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "compose-suite-pick-row",
        selected && "compose-suite-pick-row--selected",
        disabled && "opacity-50"
      )}
    >
      {kind === "file" && thumbnailUrl ? (
        <img src={thumbnailUrl} alt="" className="compose-suite-pick-row__thumb" loading="lazy" />
      ) : (
        <span className="compose-suite-pick-row__icon" aria-hidden>
          <Folder className={cn("h-4 w-4", kind === "folder" ? "text-[var(--mail-accent)]" : "text-[var(--mail-muted)]")} />
        </span>
      )}
      <span className="min-w-0 flex-1 text-left">
        <span className="block truncate text-sm text-[var(--mail-text)]">{label}</span>
        {meta ? <span className="block truncate text-[10px] text-[var(--mail-muted)]">{meta}</span> : null}
      </span>
      {kind === "file" ? (
        <span
          className={cn(
            "compose-suite-pick-row__check",
            selected && "compose-suite-pick-row__check--on"
          )}
          aria-hidden
        />
      ) : (
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[var(--mail-muted)]" aria-hidden />
      )}
    </button>
  );
}

export function ComposeSuitePickEmpty({ label }: { label: string }) {
  return <p className="px-3 py-8 text-center text-sm text-[var(--mail-muted)]">{label}</p>;
}
